import React from 'react'
import { classNames } from 'src/lib/utils/helpers'

/**
 * The design system's skeleton placeholder — `bg-zinc-100 animate-pulse`.
 * Size it with `h-*`/`w-*`; the default rounding suits text lines.
 */
export const SkeletonBar = ({ className }: { className?: string }) => (
  <div className={classNames('rounded bg-zinc-100 animate-pulse', className ?? '')} />
)

/** A settings row: icon + label on the left, its current value on the right. */
export const SkeletonSettingRow = () => (
  <div className='flex items-center justify-between gap-4 px-3 py-3.5'>
    <div className='flex items-center gap-1.5'>
      <SkeletonBar className='h-3.5 w-3.5 rounded' />
      <SkeletonBar className='h-3 w-40' />
    </div>
    <SkeletonBar className='h-3 w-16' />
  </div>
)

const DISCOUNT_LIST_SKELETON_ROWS = [
  { opacity: 'opacity-80', width: 'w-[76%]' },
  { opacity: 'opacity-60', width: 'w-[58%]' },
  { opacity: 'opacity-45', width: 'w-[84%]' },
  { opacity: 'opacity-25', width: 'w-[52%]' }
] as const

/** Loading placeholder for a discount-picker's result list (icon + label per row). */
export const SkeletonDiscountList = () => (
  <div className='flex flex-col p-1.5'>
    {DISCOUNT_LIST_SKELETON_ROWS.map((row, index) => (
      <div key={index} className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${row.opacity}`}>
        <div className='skeleton-line h-[12px] w-[12px] shrink-0' />
        <div className={`skeleton-line h-[12px] ${row.width}`} />
      </div>
    ))}
  </div>
)

/** The sticky summary card that sits to the right of both form flows. */
export const SkeletonSummaryCard = () => (
  <div className='min-w-[300px] xl:min-w-[340px] ml-auto sticky top-10'>
    <div className='rounded-2xl overflow-hidden bg-white shadow-[0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)]'>
      <div className='flex flex-col gap-2 px-6 pb-5 pt-6 border-b-1 border-black/5'>
        <div className='flex items-center gap-2'>
          <SkeletonBar className='h-3.5 w-3.5 rounded' />
          <SkeletonBar className='h-3 w-32' />
        </div>
        <SkeletonBar className='h-3 w-24' />
      </div>
      <div className='flex flex-col gap-5 px-6 py-5'>
        {[0, 1, 2, 3].map(index => (
          <div key={index} className='flex flex-col gap-1.5'>
            <SkeletonBar className='h-3 w-20' />
            <SkeletonBar className='h-3 w-2/3' />
          </div>
        ))}
      </div>
    </div>
  </div>
)
